
// BotSection.jsx
// src/components/bot-management/BotSection.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { X, Plus, LayoutGrid } from 'lucide-react';
import { botManager } from '@/managers/BotManager';
import { useConversation } from '@/managers/ConvoManager';
import { authManager } from '@/managers/AuthManager';
import BotCard from './BotCard';
import BotCatalog from './BotCatalog';
import { useTheme } from '@/managers/ThemeProvider';
import { DEFAULT_PROMPTED_BOT_ID } from '@/utils/defaultBotProfile';

/* ========================
 * useAuthSettings - Custom hook to access settings from AuthManager
 * Provides reactive access to settings stored in AuthManager
 * ======================== */
const useAuthSettings = () => {
  const [settings, setSettings] = useState(() => authManager.getSettings());

  useEffect(() => {
    const unsubscribe = authManager.subscribe((state) => {
      setSettings(state.settings);
    });
    return unsubscribe;
  }, []);

  return {
    settings,
    updateSettings: (updates) => authManager.updateSettings(updates)
  };
};

const useBotData = () => {
    const [botState, setBotState] = useState(() => botManager.getState());
    useEffect(() => {
        const unsubscribe = botManager.subscribe(setBotState);
        return () => unsubscribe();
    }, []);

    return {
        allBots: botState.allBots || [],
        activeBotConfig: botState.activeBotConfig,
        availablePlatformsMap: botState.platformsWithActiveKeysMap || {},
        isLoading: !botState.isInitialized,
        isInitialized: botState.isInitialized,
    };
};

const useAuthData = () => {
    const [authState, setAuthState] = useState(() => authManager.getState());
    useEffect(() => {
        const unsubscribe = authManager.subscribe(setAuthState);
        return () => unsubscribe();
    }, []);
    return {
        userTier: authState.tier,
        userId: authState.user?.uid,
    };
};

const CreateCustomBot = ({ onManageBots, onCreateCustom }) => {
    const { isLightTheme } = useTheme();
    const { userTier } = useAuthData();
    const proLevelTiers = ['Pro', 'Beta', 'Admin', 'Pro Secure'];
    const hasProAccess = proLevelTiers.includes(userTier);

    return (
        <div className="px-3 py-2 flex gap-2">
            <button 
                onClick={hasProAccess ? onCreateCustom : undefined} 
                disabled={!hasProAccess}
                className={`flex-1 flex items-center justify-between px-4 py-1 rounded-md transition-all duration-200 border
                    ${isLightTheme ? 'bg-background border-border-subtle' : 'bg-surface-raised border-border-subtle'}
                    ${hasProAccess ? (isLightTheme ? 'hover:bg-background-dark/60 cursor-pointer' : 'hover:bg-surface-dark/60 cursor-pointer') : 'cursor-not-allowed'}`}
            >
                <div className="flex items-center gap-2.5">
                    <Plus size={13} className="text-on-surface-dim" />
                    <span className="text-[11px] text-on-surface-dim">Create Custom Bot</span>
                </div>
            </button>
            <button 
                onClick={onManageBots} 
                className={`px-3 py-2.5 rounded-md transition-all duration-200 border
                    ${isLightTheme ? 'bg-background border-border-subtle hover:bg-background-dark/60' : 'bg-surface-raised border-border-subtle hover:bg-surface-dark/60'}`}
                title="Manage All Bots & Models"
            >
                <LayoutGrid size={15} className="text-on-surface-dim" />
            </button>
        </div>
    );
};

const MoreBotsButton = ({ onClick }) => {
    const { isLightTheme } = useTheme();
    
    return (
        <div 
            onClick={onClick}
            className={`px-3 py-2.5 border-t border-border-subtle backdrop-blur-sm transition-all duration-200 cursor-pointer flex items-center justify-between
                ${isLightTheme ? 'bg-surface/95 hover:bg-background-dark/60' : 'bg-surface/95 hover:bg-surface-dark/60'}`}
        >
            <span className="text-xs text-on-surface-dim">Manage Bots & Models...</span>
            <LayoutGrid size={14} className="text-on-surface-dim" />
        </div>
    );
};

const BotSection = ({ onClose, containerHeight }) => {
    const { isLightTheme } = useTheme();
    const { 
        allBots, 
        availablePlatformsMap, 
        isInitialized,
        activeBotConfig, 
    } = useBotData(); 
    const { userId } = useAuthData();
    
    const { createConversation } = useConversation(); 
    const { settings, updateSettings } = useAuthSettings();
    
    const pinnedBotsFromSettings = useMemo(() => settings.zPreferences?.pinnedBots || [], [settings.zPreferences?.pinnedBots]);

    const [showCatalog, setShowCatalog] = useState(false);
    const [editingBotIdForCatalog, setEditingBotIdForCatalog] = useState(null); 

    const currentBotId = activeBotConfig?.botId;
    const currentModelId = activeBotConfig?.modelId;

    useEffect(() => {
        // Ensure the default prompted profile is pinned when settings are available.
        if (isInitialized && allBots.length > 0 && settings.zPreferences?.pinnedBots !== undefined) { // Check settings.pinnedBots is not undefined
            const defaultBot = allBots.find(b => b.id === DEFAULT_PROMPTED_BOT_ID);
            if (!defaultBot) return; 

            const currentPinnedBots = Array.isArray(settings.zPreferences.pinnedBots) ? settings.zPreferences.pinnedBots : [];
            if (!currentPinnedBots.includes(defaultBot.id)) {
                const newPinnedList = [...new Set([...currentPinnedBots, defaultBot.id])];
                updateSettings({ zPreferences: { pinnedBots: newPinnedList } });
            }
        } else if (isInitialized && allBots.length > 0 && settings.zPreferences?.pinnedBots === undefined) {
            const defaultBot = allBots.find(b => b.id === DEFAULT_PROMPTED_BOT_ID);
            if (defaultBot) {
                 updateSettings({ zPreferences: { pinnedBots: [defaultBot.id] } });
            }
        }
    }, [isInitialized, allBots, settings.zPreferences?.pinnedBots, updateSettings]);


    const handleModelSelection = async (botOfCard, modelClicked) => {
        if (currentBotId === botOfCard.id && currentModelId === modelClicked.id) {
            onClose?.();
            return;
        }
        botManager.setCurrentBotAndModel(botOfCard.id, modelClicked.id);

        try {
            await createConversation({
                botId: botOfCard.id, 
                modelId: modelClicked.id,
                customSystemMessage: botOfCard.customSystemMessage || null
            });
            onClose?.();
        } catch (error) {
            console.error("Error selecting model and creating conversation:", error);
        }
    };
    
    const handleDirectNewChatRequest = async (bot, model) => {
        if (!bot || !model) return;
        try {
            botManager.setCurrentBotAndModel(bot.id, model.id);
            await createConversation({ 
                botId: bot.id,
                modelId: model.id,
                customSystemMessage: bot.customSystemMessage || null,
                forceNew: true 
            });
            onClose?.();
        } catch (error) {
            console.error("Error creating new chat directly:", error);
        }
    };

    const toggleFavorite = (botIdToToggle) => {
        // Prevent any form submission or navigation
        try {
            if (window.event) {
                window.event.preventDefault();
                window.event.stopPropagation();
                window.event.stopImmediatePropagation();
            }
        } catch (e) {}
        
        // Return early to test if the refresh stops
        console.log('🔍 toggleFavorite called, returning early to test');
        return;
        
        // Comment out the rest temporarily
        /*
        const currentPinned = Array.isArray(settings.zPreferences?.pinnedBots) ? settings.zPreferences.pinnedBots : [];
        let newPinnedBotsList;
        const shelbyBotExists = allBots.some(b => b.id === 'shelby');
    
        if (botIdToToggle === 'shelby') {
            if (shelbyBotExists && !currentPinned.includes('shelby')) {
                 updateSettings({ zPreferences: { pinnedBots: [...new Set([...currentPinned, 'shelby'])] } });
            }
            return; 
        }
    
        if (currentPinned.includes(botIdToToggle)) {
            newPinnedBotsList = currentPinned.filter(id => id !== botIdToToggle);
        } else {
            newPinnedBotsList = [...currentPinned, botIdToToggle];
        }
    
        if (shelbyBotExists && !newPinnedBotsList.includes('shelby')) {
            newPinnedBotsList.push('shelby');
        }
        
        updateSettings({ zPreferences: { pinnedBots: [...new Set(newPinnedBotsList)] } });
        */
    };
    const sortedBotsForDisplay = useMemo(() => {
        if (!isInitialized || !allBots || allBots.length === 0) return [];
    
        const displayList = [];
        const addedIds = new Set();
        // Use pinnedBotsFromSettings which defaults to [] if settings.pinnedBots is undefined/null
        const currentPinnedForSort = pinnedBotsFromSettings; 
    
        const defaultBot = allBots.find(bot => bot.id === DEFAULT_PROMPTED_BOT_ID);
        if (defaultBot) {
            displayList.push(defaultBot);
            addedIds.add(defaultBot.id);
        }
    
        if (activeBotConfig && activeBotConfig.botId !== DEFAULT_PROMPTED_BOT_ID) {
            const activeBotDefinition = allBots.find(b => b.id === activeBotConfig.botId);
            if (activeBotDefinition && !addedIds.has(activeBotDefinition.id)) {
                displayList.push(activeBotDefinition);
                addedIds.add(activeBotDefinition.id);
            }
        }
    
        currentPinnedForSort.forEach(pinnedBotId => {
            if (pinnedBotId !== DEFAULT_PROMPTED_BOT_ID && !addedIds.has(pinnedBotId)) {
                const pinnedBot = allBots.find(bot => bot.id === pinnedBotId);
                if (pinnedBot) {
                    displayList.push(pinnedBot);
                    addedIds.add(pinnedBotId);
                }
            }
        });
    
        allBots.forEach(bot => {
            const isUserOwnedCustomBot = bot.isCustomBot && userId && bot.id.startsWith(`custom_${userId}_`);
            if (isUserOwnedCustomBot && !addedIds.has(bot.id)) {
                displayList.push(bot);
                addedIds.add(bot.id);
            }
        });
    
        const vanillaBot = allBots.find(bot => bot.id === 'vanillabot');
        if (vanillaBot && !addedIds.has(vanillaBot.id)) {
            displayList.push(vanillaBot);
        }
        return displayList;
    }, [allBots, isInitialized, activeBotConfig, pinnedBotsFromSettings, userId]);


    if (!isInitialized) { 
        return ( <div className="flex items-center justify-center h-full"><span className="text-on-surface-dim">Loading...</span></div> ); 
    }

    const handleOpenCatalogAndCloseSidebar = (botContext = null) => { 
        let botIdToSetForEditing = null;
        if (botContext && typeof botContext === 'object' && botContext.id) {
            const isUserOwnedCustomBot = botContext.isCustomBot && 
                                         userId && 
                                         botContext.id.startsWith(`custom_${userId}_`);
            if (isUserOwnedCustomBot) {
                botIdToSetForEditing = botContext.id;
            }
        }
        setEditingBotIdForCatalog(botIdToSetForEditing);
        setShowCatalog(true); 
        onClose?.(); 
    };
    
    const handleOpenKeysManagement = () => { 
        setEditingBotIdForCatalog(null); 
        setShowCatalog(true);
        onClose?.();
    };

    return (
        <>
            <div className="flex flex-col animate-fadeIn" style={{ height: containerHeight || '100%' }}>
                <div className={`flex items-center justify-between p-3 border-b border-border-subtle`}>
                    <h2 className="text-xs font-semibold text-on-surface">Select Bot</h2>
                    <button 
                        onClick={onClose} 
                        className={`p-1 rounded-md transition-colors ${isLightTheme ? 'hover:bg-background-dark' : 'hover:bg-surface-dark'}`}
                    >
                        <X size={16} className="text-on-surface-dim hover:text-on-surface"/>
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar"> 
                    {sortedBotsForDisplay.map((bot) => {
                        const isFav = bot.id === DEFAULT_PROMPTED_BOT_ID ? true : pinnedBotsFromSettings.includes(bot.id);
                        return (
                            <BotCard 
                                key={bot.id} 
                                bot={bot} 
                                onSelectModel={handleModelSelection}
                                onToggleFavorite={() => toggleFavorite(bot.id)}
                                isFavorite={isFav}
                                isActive={currentBotId === bot.id} 
                                defaultExpanded={currentBotId === bot.id || (bot.id === DEFAULT_PROMPTED_BOT_ID && !currentBotId)} 
                                availablePlatforms={availablePlatformsMap} 
                                onOpenKeys={handleOpenKeysManagement} 
                                createNewChat={handleDirectNewChatRequest}
                                onOpenCatalog={() => handleOpenCatalogAndCloseSidebar(bot)} 
                            />
                        );
                    })}
                    {sortedBotsForDisplay.length === 0 && ( 
                        <div className="flex items-center justify-center h-32">
                            <span className="text-on-surface-dim text-sm">No bots available</span>
                        </div>
                    )}
                    <MoreBotsButton onClick={() => handleOpenCatalogAndCloseSidebar()} />
                </div>
                <CreateCustomBot 
                    onManageBots={() => handleOpenCatalogAndCloseSidebar()} 
                    onCreateCustom={() => {
                        setEditingBotIdForCatalog(null); 
                        setShowCatalog(true); 
                        onClose?.();
                    }} 
                />
            </div>
            {showCatalog && (
                <BotCatalog 
                    onClose={() => setShowCatalog(false)} 
                    initialEditingBotId={editingBotIdForCatalog}
                />
            )}
        </>
    );
};

export default BotSection;